import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { routes, type SelectPropertyTypeLocationState } from "@/app/navigation/routes";
import { useAuth } from "@/app/providers/useAuth";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { useAddress } from "@/shared/hooks";
import { useAddressListViewModel } from "@/shared/components/common/AddressSelectPill/AdrressListviewmodel";
import {
  propertyService,
  propertySupportService,
} from "@/features/properties/model/propertyServices";
import { toastService } from "@/shared/toast";
import type {
  CrimeIncidentsResponse,
  SexOffendersResponse,
  SexOffendersOrCrimeIncidentsPayload,
} from "@/features/properties/model/propertiesTypes";

export type RecordItem = {
  id: number;
  name: string;
  gender: string;
  age: number;
  imageUrl?: string;
};

export type CommunityTab = "crime" | "offenders";

export type CommunityMapMarker = {
  lat: number;
  lng: number;
  color?: string;
  type?: "offender" | "crime";
  data?: unknown;
};

const defaultMapCenter = { lat: 30.2672, lng: -97.7431 };
const PROPERTY_TAX_SHARE_BASE_URL = "https://theandyapp.com/property-tax-data";
const TINY_URL_API_KEY = import.meta.env.VITE_TINY_URL_API_KEY as string | undefined;

export const useHomePageViewModal = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { selectedAddress: selectedPropertyId, setSelectedAddress: setSelectedPropertyId } =
    useAddress();
  const { options: addressOptions } = useAddressListViewModel();
  const [isMapModalOpen, setIsMapModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<CommunityTab>("crime");
  const recordsListRef = useRef<HTMLDivElement | null>(null);
  const [hasRecordOverflow, setHasRecordOverflow] = useState(false);
  const [recordsListMaxHeight, setRecordsListMaxHeight] = useState<number | null>(null);
  const [isServiceExpansionModalOpen, setIsServiceExpansionModalOpen] = useState(false);
  const [isProtestApplicabilityLoading, setIsProtestApplicabilityLoading] = useState(false);
  const [isSharingPropertyTaxData, setIsSharingPropertyTaxData] = useState(false);
  const [isPowerbillEligible, setIsPowerbillEligible] = useState<boolean>(false);
  const [isSettlementWaiverViewerOpen, setIsSettlementWaiverViewerOpen] = useState(false);
  const [isSettlementWaiverDownloading, setIsSettlementWaiverDownloading] = useState(false);

  useEffect(() => {
    if (selectedPropertyId) return;
    const first = addressOptions.at(0);
    if (!first) return;
    setSelectedPropertyId(first.value);
  }, [addressOptions, selectedPropertyId, setSelectedPropertyId]);

  const selectedPropertyOption = useMemo(
    () => addressOptions.find((o) => o.value === selectedPropertyId),
    [addressOptions, selectedPropertyId]
  );
  const effectiveAddress = selectedPropertyOption?.label || "694 Center St, Chicopee, MA";

  const {
    data: propertyDetails,
    isLoading: isPropertyDetailsLoading,
    error: propertyDetailsError,
  } = useQuery({
    queryKey: [QUERY_KEYS.PROPERTY_DETAILS, selectedPropertyId],
    queryFn: () => propertyService.getPropertyById(selectedPropertyId),
    enabled: Boolean(selectedPropertyId && selectedPropertyId !== "__add__"),
  });
  const settlementWaiverDocumentUrl = useMemo(() => {
    // console.log(propertyDetails,"journeyItems2434325")
    const docs = propertyDetails?.documents?.property_settlement_waiver_document;
    if (!Array.isArray(docs) || docs.length === 0) return null;

    const activeDoc = docs.find(
      (doc) =>
        !doc.is_deleted &&
        doc.settlement_waiver_document?.trim() &&
        (doc.status ?? "active").toLowerCase() === "active"
    );

    return (
      activeDoc?.settlement_waiver_document?.trim() ??
      docs[0]?.settlement_waiver_document?.trim() ??
      null
    );
  }, [propertyDetails?.documents?.property_settlement_waiver_document]);

  const activeRefundInvoice = useMemo(() => {
    const invoices = propertyDetails?.prop_refund_invoices;
    if (!Array.isArray(invoices) || invoices.length === 0) return null;

    const openInvoice = invoices.find(
      (invoice) => (invoice.payment_status ?? "").toLowerCase() === "open"
    );

    return openInvoice ?? invoices[0] ?? null;
  }, [propertyDetails?.prop_refund_invoices]);

  const refundInvoicePaymentLink = activeRefundInvoice?.payment_link?.trim() || null;
  const refundInvoicePdfLink = activeRefundInvoice?.invoice_link?.trim() || null;
  const showRefundInvoice = Boolean(
    activeRefundInvoice && (refundInvoicePaymentLink || refundInvoicePdfLink)
  );
  const isRefundInvoicePayable =
    (activeRefundInvoice?.payment_status ?? "").toLowerCase() === "open" &&
    Boolean(refundInvoicePaymentLink);

  const showSettlementWaiverDocument = Boolean(settlementWaiverDocumentUrl);

  const handleViewSettlementWaiverDocument = useCallback(() => {
    if (!settlementWaiverDocumentUrl) return;
    setIsSettlementWaiverViewerOpen(true);
  }, [settlementWaiverDocumentUrl]);

  const closeSettlementWaiverViewer = useCallback(() => {
    setIsSettlementWaiverViewerOpen(false);
  }, []);

  const handleDownloadSettlementWaiverDocument = useCallback(async () => {
    if (!settlementWaiverDocumentUrl || isSettlementWaiverDownloading) return;

    setIsSettlementWaiverDownloading(true);
    try {
      const response = await fetch(settlementWaiverDocumentUrl);
      if (!response.ok) {
        throw new Error("Failed to download document");
      }

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = "settlement-waiver-document.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
      toastService.show("Settlement waiver document downloaded.", "success");
    } catch (error) {
      console.error("Error downloading settlement waiver document:", error);
      toastService.show("Could not download settlement waiver document.", "error");
    } finally {
      setIsSettlementWaiverDownloading(false);
    }
  }, [isSettlementWaiverDownloading, settlementWaiverDocumentUrl]);

  const handlePayRefundInvoice = useCallback(() => {
    if (!refundInvoicePaymentLink) return;
    window.open(refundInvoicePaymentLink, "_blank", "noopener,noreferrer");
  }, [refundInvoicePaymentLink]);

  const handleViewRefundInvoice = useCallback(() => {
    // payment_link works for both open and paid (hosted invoice / receipt)
    const link = refundInvoicePaymentLink || refundInvoicePdfLink;
    if (!link) return;
    window.open(link, "_blank", "noopener,noreferrer");
  }, [refundInvoicePaymentLink, refundInvoicePdfLink]);

  const coordinates = useMemo(() => {
    const lat = propertyDetails?.address?.latitude;
    const lon = propertyDetails?.address?.longitude;
    if (typeof lat !== "number" || typeof lon !== "number") return null;
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
    return { lat, lon };
  }, [propertyDetails?.address?.latitude, propertyDetails?.address?.longitude]);

  const city = propertyDetails?.address?.city || "";
  const zip = propertyDetails?.address?.zip || "";

  const communityPayload = useMemo((): Partial<SexOffendersOrCrimeIncidentsPayload> | null => {
    if (!coordinates) return null;
    const now = new Date();
    const oneYearAgo = new Date();
    oneYearAgo.setFullYear(now.getFullYear() - 1);
    return {
      lat: coordinates.lat,
      lon: coordinates.lon,
      distance: 5,
      cityname: city,
      datetime_ini: oneYearAgo.toISOString(),
      datetime_end: now.toISOString(),
      page: 1,
      zipcode: zip,
    };
  }, [city, coordinates, zip]);

  const {
    data: crimeIncidentsData,
    isLoading: isCrimeLoading,
    error: crimeError,
  } = useQuery<CrimeIncidentsResponse>({
    queryKey: [QUERY_KEYS.PROPERTY_DETAILS, "crime", coordinates?.lat, coordinates?.lon, zip],
    // Fetch on home load once location is known (not only when Crime tab is active).
    enabled: Boolean(communityPayload),
    queryFn: async () => {
      if (!communityPayload) throw new Error("Location coordinates not available");
      try {
        return await propertySupportService.getCrimeIncidentsByLocation(communityPayload);
      } catch {
        return await propertySupportService.getCrimeIncidents(communityPayload);
      }
    },
  });

  const {
    data: sexOffendersData,
    isLoading: isOffendersLoading,
    error: offendersError,
  } = useQuery<SexOffendersResponse>({
    queryKey: [QUERY_KEYS.PROPERTY_DETAILS, "offenders", coordinates?.lat, coordinates?.lon, zip],
    // Fetch on home load once location is known (not only when Offenders tab is active).
    enabled: Boolean(communityPayload),
    queryFn: async () => {
      if (!communityPayload) throw new Error("Location coordinates not available");
      try {
        return await propertySupportService.getSexOffendersByLocation(communityPayload);
      } catch (err) {
        if (zip) {
          return await propertySupportService.getSexOffendersByZip({
            ...communityPayload,
            zipcode: zip,
          });
        }
        throw err;
      }
    },
  });

  const isCommunityLoading = activeTab === "crime" ? isCrimeLoading : isOffendersLoading;
  const communityError = (activeTab === "crime" ? crimeError : offendersError) as Error | null;

  const records = useMemo((): RecordItem[] => {
    if (activeTab === "crime") {
      const incidents = crimeIncidentsData?.incidents || [];
      return incidents.map((i, idx) => ({
        id: idx + 1,
        name: i.incident_offense_description || i.incident_offense || "Crime Incident",
        gender: i.incident_source_name || "Report",
        age: 0,
      }));
    }
    const offenders = sexOffendersData?.sex_offenders || [];
    return offenders.map((o, idx) => ({
      id: idx + 1,
      name:
        o.sex_offender_name ||
        [o.sex_offender_first_name, o.sex_offender_last_name].filter(Boolean).join(" ") ||
        "Sex Offender",
      gender: o.sex_offender_sex || "Unknown",
      age: o.sex_offender_age ? Number(o.sex_offender_age) : 0,
      imageUrl: o.sex_offender_image_url || undefined,
    }));
  }, [activeTab, crimeIncidentsData?.incidents, sexOffendersData?.sex_offenders]);

  const crimeCount = crimeIncidentsData?.incidents?.length ?? 0;
  const offendersCount = sexOffendersData?.sex_offenders?.length ?? 0;

  const communityMarkers = useMemo((): CommunityMapMarker[] => {
    if (activeTab === "crime") {
      const incidents = crimeIncidentsData?.incidents || [];
      return incidents
        .map((i) => ({
          lat: Number(i.incident_latitude),
          lng: Number(i.incident_longitude),
          color: "#ef4444",
          type: "crime" as const,
          data: i,
        }))
        .filter((m) => Number.isFinite(m.lat) && Number.isFinite(m.lng));
    }
    const offenders = sexOffendersData?.sex_offenders || [];
    return offenders
      .map((o) => ({
        lat: Number(o.sex_offender_lat),
        lng: Number(o.sex_offender_lon),
        color: "#eab308",
        type: "offender" as const,
        data: o,
      }))
      .filter((m) => Number.isFinite(m.lat) && Number.isFinite(m.lng));
  }, [activeTab, crimeIncidentsData?.incidents, sexOffendersData?.sex_offenders]);

  const mapCenter = useMemo(
    () => (coordinates ? { lat: coordinates.lat, lng: coordinates.lon } : defaultMapCenter),
    [coordinates]
  );

  const formatCurrency = useCallback((value: number | null | undefined) => {
    if (value === null || value === undefined || Number.isNaN(value)) return "—";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    }).format(value);
  }, []);

  const protestYear = new Date().getFullYear();
  const hasCurrentYearPaymentProtest = useMemo(
    () => Boolean(propertyDetails?.protestPayments?.[protestYear]?.paid),
    [propertyDetails, protestYear]
  );
  const protestStatus = useMemo(() => {
    if (propertyDetails?.protest_status !== "filed" || propertyDetails?.protest_status === null)
      return false;
    return true;
  }, [propertyDetails]);
  const isResidentialProperty = useMemo(() => {
    const type = (propertyDetails?.property_type || "").toLowerCase();
    const rawType = (propertyDetails?.property_raw_type || "").toLowerCase();
    return type.includes("residential") || rawType.includes("residential");
  }, [propertyDetails?.property_raw_type, propertyDetails?.property_type]);

  const marketValue = propertyDetails?.tax_info?.market_value;
  const assessedValue =
    propertyDetails?.tax_info?.assessed_value ?? propertyDetails?.assessed_value;
  const totalTaxBill = propertyDetails?.tax_info?.tax_amount;
  const amountSaved = useMemo(() => {
    const savedAmount = propertyDetails?.amount_saved;
    if (savedAmount !== null && savedAmount !== undefined) {
      return savedAmount;
    }
    const taxAmount = propertyDetails?.tax_info?.tax_amount;
    return taxAmount && taxAmount > 0 ? Math.round(taxAmount * 0.08) : 0;
  }, [propertyDetails?.amount_saved, propertyDetails?.tax_info?.tax_amount]);

  const potentialSavings = useMemo(() => {
    if (amountSaved !== null && amountSaved !== undefined && Number.isFinite(Number(amountSaved))) {
      return Number(amountSaved);
    }
    return totalTaxBill ? Math.round(Number(totalTaxBill) * 0.08) : 0;
  }, [amountSaved, totalTaxBill]);

  const isPropertyOwner = useMemo(() => {
    return propertyDetails?.property_user_type === "owner";
  }, [propertyDetails]);

  const isPaymentCompleted = useMemo(() => {
    return propertyDetails?.payment_status === "paid";
  }, [propertyDetails]);

  const hasProtestFiled = useMemo(() => {
    const status = propertyDetails?.protest_status;
    return status === "filed" || status === "inprogress";
  }, [propertyDetails]);
  // inprogress

  //  const hasProtestinprogress = useMemo(() => {
  //   return propertyDetails?.protest_status === "inprogress";
  // }, [propertyDetails]);

  const isProtestRejected = useMemo(() => {
    return propertyDetails?.protest_status === "reject";
  }, [propertyDetails]);

  const isProtestApproved = useMemo(() => {
    return propertyDetails?.protest_status === "approved";
  }, [propertyDetails]);

  const homesteadFiled = useMemo(() => {
    return (
      propertyDetails?.homestead_status !== "not_filed" &&
      propertyDetails?.homestead_status !== null
    );
  }, [propertyDetails]);

  const hasPaymentStatus = useMemo(() => {
    return !!propertyDetails?.payment_status;
  }, [propertyDetails]);

  useEffect(() => {
    const el = recordsListRef.current;
    if (!el) return;
    const update = () => setHasRecordOverflow(el.scrollHeight > el.clientHeight);
    update();
    if (typeof ResizeObserver !== "undefined") {
      const ro = new ResizeObserver(update);
      ro.observe(el);
      window.addEventListener("resize", update);
      return () => {
        ro.disconnect();
        window.removeEventListener("resize", update);
      };
    }
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  useLayoutEffect(() => {
    const el = recordsListRef.current;
    if (!el) return;
    const computeMaxHeightForFirstN = (n: number) => {
      const children = Array.from(el.children) as HTMLElement[];
      if (children.length <= n) {
        setRecordsListMaxHeight(null);
        return;
      }
      const style = window.getComputedStyle(el);
      const gap = Number.parseFloat(style.rowGap || style.gap || "0") || 0;
      let total = 0;
      for (let i = 0; i < n; i += 1) {
        total += children[i]?.offsetHeight ?? 0;
        if (i < n - 1) total += gap;
      }
      setRecordsListMaxHeight(total);
    };
    const raf = window.requestAnimationFrame(() => computeMaxHeightForFirstN(8));
    return () => window.cancelAnimationFrame(raf);
  }, [records.length]);

  const serviceExpansionDefaults = useMemo(
    () => ({
      name: [user?.first_name, user?.last_name].filter(Boolean).join(" ").trim(),
      email: user?.email ?? "",
      phone: user?.phone ?? "",
      state: propertyDetails?.address?.state ?? "",
      city: propertyDetails?.address?.city ?? "",
    }),
    [user, propertyDetails?.address?.state, propertyDetails?.address?.city]
  );

  const handleFileProtest = useCallback(async () => {
    if (!selectedPropertyId || selectedPropertyId === "__add__") {
      window.alert("Please select a property.");
      return;
    }

    setIsProtestApplicabilityLoading(true);
    try {
      const result = await propertyService.getProtestApplicability(selectedPropertyId);
      if (!result.applicable) {
        setIsServiceExpansionModalOpen(true);
        return;
      }
      if (hasCurrentYearPaymentProtest) {
        navigate(routes.selectPropertyType);
        return;
      }
      navigate(routes.protestPayment);
    } catch (e) {
      console.error(e);
      window.alert(
        e instanceof Error ? e.message : "Could not verify protest availability. Please try again."
      );
    } finally {
      setIsProtestApplicabilityLoading(false);
    }
  }, [hasCurrentYearPaymentProtest, navigate, selectedPropertyId]);

  const handlePaymentNavigation = useCallback(() => {
    navigate(routes.protestPayment);
  }, [navigate]);

  const handleSignDocumentsNavigation = useCallback(() => {
    navigate(routes.selectPropertyType);
  }, [navigate]);

  const handleSupportingDocumentsNavigation = useCallback(() => {
    navigate(routes.selectPropertyType, {
      state: { openProtestSupportingDocuments: true } satisfies SelectPropertyTypeLocationState,
    });
  }, [navigate]);

  const handleHomesteadNavigation = useCallback(() => {
    navigate(routes.homesteadExemption);
  }, [navigate]);

  const handleHomesteadExemption = useCallback(() => {
    navigate(routes.homesteadExemption);
  }, [navigate]);

  const closeServiceExpansionModal = useCallback(() => {
    setIsServiceExpansionModalOpen(false);
  }, []);

  const isProtestCompleted = isPaymentCompleted && hasProtestFiled;

  type JourneyItem = {
    key: string;
    title: string;
    subtitle: string;
    visible: boolean;
    onClick: () => void;
  };

  const journeyItems: JourneyItem[] = [
    // STEP 1 — PAYMENT
    {
      key: "payment",

      title: isProtestCompleted
        ? "Protest Filed Successfully"
        : isPaymentCompleted
          ? "Payment Completed"
          : "Make Payment",

      subtitle: isProtestCompleted
        ? "Your protest has been submitted successfully"
        : isPaymentCompleted
          ? "Your protest payment has been completed"
          : "Complete payment to continue your protest",

      visible: true,

      onClick: () => {
        if (isPaymentCompleted) return;
        handlePaymentNavigation();
      },
    },

    // STEP 2 — SIGN DOCUMENTS
    {
      key: "sign-documents",

      title: "Sign Documents",

      subtitle: "Review and complete digital signature",

      visible: isPaymentCompleted && !hasProtestFiled,

      onClick: handleSignDocumentsNavigation,
    },

    // STEP 3 — SUPPORTING DOCUMENTS
    {
      key: "supporting-documents",

      title: "Supporting Documents",

      subtitle: "Upload files to support your protest",

      visible: hasPaymentStatus && hasProtestFiled,

      onClick: handleSupportingDocumentsNavigation,
    },

    // STEP 4 — HOMESTEAD
    {
      key: "homestead",

      title: homesteadFiled ? "Homestead Filed" : "Homestead Exemption",

      subtitle: homesteadFiled
        ? "Your homestead exemption has been submitted"
        : "Save on your property taxes",

      visible: isResidentialProperty && hasProtestFiled,

      onClick: handleHomesteadNavigation,
    },
  ];

  const visibleJourneyItems = journeyItems.filter((item) => item.visible);

  const generatePropertyTaxSharingURL = useCallback(() => {
    if (!propertyDetails) return PROPERTY_TAX_SHARE_BASE_URL;

    const params = new URLSearchParams();
    const { address } = propertyDetails;

    if (address?.city) params.set("city", address.city);
    if (address?.zip) params.set("zip", address.zip);
    if (address?.full_address) params.set("address", address.full_address);
    if (marketValue !== null && marketValue !== undefined)
      params.set("marketValue", String(marketValue));
    if (assessedValue !== null && assessedValue !== undefined)
      params.set("assessedValue", String(assessedValue));
    if (totalTaxBill !== null && totalTaxBill !== undefined)
      params.set("taxValue", String(totalTaxBill));
    if (potentialSavings !== null && potentialSavings !== undefined)
      params.set("overTaxation", String(potentialSavings));
    if (user?.first_name) params.set("sharedBy", user.first_name);

    const query = params.toString();
    return query ? `${PROPERTY_TAX_SHARE_BASE_URL}?${query}` : PROPERTY_TAX_SHARE_BASE_URL;
  }, [
    assessedValue,
    marketValue,
    potentialSavings,
    propertyDetails,
    totalTaxBill,
    user?.first_name,
  ]);

  const shortenURL = useCallback(async (url: string): Promise<string | null> => {
    if (!TINY_URL_API_KEY) return null;

    try {
      const response = await fetch("https://api.tinyurl.com/create", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${TINY_URL_API_KEY}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          url,
          domain: "tinyurl.com",
        }),
      });

      if (!response.ok) return null;

      const payload = (await response.json()) as { data?: { tiny_url?: string } };
      return payload?.data?.tiny_url ?? null;
    } catch (error) {
      console.error("Error shortening URL:", error);
      return null;
    }
  }, []);

  const buildPropertyTaxShareMessage = useCallback(
    (url: string) => {
      const sharedBy = user?.first_name || "Your Neighbor";
      const propertyAddress =
        propertyDetails?.address?.full_address || effectiveAddress || "Property";
      return (
        `${sharedBy} shared Property Tax Data with you:\n\n` +
        `- Property: ${propertyAddress}\n` +
        `- Market Value: ${formatCurrency(marketValue)}\n` +
        `- Assessed Value: ${formatCurrency(assessedValue)}\n` +
        `- Tax Value: ${formatCurrency(totalTaxBill)}\n` +
        `- Potential Over Taxation: ${formatCurrency(potentialSavings)}\n` +
        `- Crime Report Count: ${crimeCount}\n` +
        `- Sex Offenders Count: ${offendersCount}\n\n` +
        `${url}`
      );
    },
    [
      assessedValue,
      crimeCount,
      effectiveAddress,
      formatCurrency,
      marketValue,
      offendersCount,
      potentialSavings,
      propertyDetails?.address?.full_address,
      totalTaxBill,
      user?.first_name,
    ]
  );

  const handleSharePropertyTaxData = useCallback(async () => {
    try {
      setIsSharingPropertyTaxData(true);

      const sharingURL = generatePropertyTaxSharingURL();
      const shortURL = await shortenURL(sharingURL);
      const finalURL = shortURL || sharingURL;
      const message = buildPropertyTaxShareMessage(finalURL);

      if (navigator.share) {
        await navigator.share({
          title: "Property Tax Data",
          text: message,
          url: finalURL,
        });
        toastService.show("Property tax data shared.", "success");
        return;
      }

      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(message);
        toastService.show("Share details copied to clipboard.", "success");
        return;
      }

      window.prompt("Copy and share this property tax summary:", message);
    } catch (error) {
      if (error instanceof Error && error.name === "AbortError") {
        return;
      }
      console.error("Error sharing property tax data:", error);
      toastService.show("Could not share property tax data.", "error");
    } finally {
      setIsSharingPropertyTaxData(false);
    }
  }, [buildPropertyTaxShareMessage, generatePropertyTaxSharingURL, shortenURL]);

  return {
    isMapModalOpen,
    setIsMapModalOpen,
    activeTab,
    setActiveTab,
    recordsListRef,
    hasRecordOverflow,
    recordsListMaxHeight,
    effectiveAddress,
    propertyDetailsError,
    isPropertyDetailsLoading,
    marketValue,
    assessedValue,
    totalTaxBill,
    amountSaved,
    potentialSavings,
    formatCurrency,
    handleFileProtest,
    mapCenter,
    communityMarkers,
    isCommunityLoading,
    communityError,
    records,
    crimeCount,
    offendersCount,
    protestStatus,
    isResidentialProperty,
    isPropertyOwner,
    handleHomesteadExemption,
    isServiceExpansionModalOpen,
    closeServiceExpansionModal,
    serviceExpansionDefaults,
    isProtestApplicabilityLoading,
    isSharingPropertyTaxData,
    handleSharePropertyTaxData,
    setIsPowerbillEligible,
    isPowerbillEligible,
    journeyItems: visibleJourneyItems,
    isPaymentCompleted,
    hasProtestFiled,
    isProtestRejected,
    isProtestApproved,
    homesteadFiled,
    showSettlementWaiverDocument,
    settlementWaiverDocumentUrl,
    isSettlementWaiverViewerOpen,
    closeSettlementWaiverViewer,
    handleViewSettlementWaiverDocument,
    handleDownloadSettlementWaiverDocument,
    isSettlementWaiverDownloading,
    showRefundInvoice,
    activeRefundInvoice,
    isRefundInvoicePayable,
    handlePayRefundInvoice,
    handleViewRefundInvoice,
  };
};
